import React, { useState, useEffect, useRef } from 'react';
import {
    FaBullhorn, FaWifi, FaScroll, FaUsers, FaSlidersH,
    FaFilter, FaClock, FaAddressBook
} from "react-icons/fa";
import { FaHandPointLeft, FaPaperPlane } from "react-icons/fa6";

const API_URL = process.env.REACT_APP_API_URL || "https://wa-api.globebot.io";
function decodeToken(token) {
    if (!token) return {};
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
        );
        return JSON.parse(jsonPayload);
    } catch { return {}; }
}

function getUserIdFromToken() {
    if (typeof window !== 'undefined' && typeof window.locatorgarsotrga === 'function') {
        const rawToken = window.locatorgarsotrga("token");
        if (rawToken) {
            const decoded = decodeToken(rawToken);
            return { userId: decoded.id || decoded.userId || decoded._id, token: rawToken };
        }
    }
    const rawToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (rawToken) {
        const decoded = decodeToken(rawToken);
        return { userId: decoded.id || decoded.userId || decoded._id, token: rawToken };
    }
    return { userId: null, token: null };
}

function VerifySend({ campaignData, templateData, token, onPrev, onSuccess }) {
    const [launching,   setLaunching]   = useState(false);
    const [launched,    setLaunched]    = useState(false);   // true once queue accepted
    const [queueStats,  setQueueStats]  = useState(null);    // live stats from /campaign/stats
    const [launchError, setLaunchError] = useState(null);
    const [schedule, setSchedule] = useState(false);          // New: scheduling mode
    const [scheduledTime, setScheduledTime] = useState("");   // New: scheduled datetime local value
    const pollRef = useRef(null);
    const { userId } = getUserIdFromToken();

    // Derived variables
    const uniqueNumbers  = [...new Set(
        campaignData.numbers
            .map(n => String(n).trim().replace(/\D/g, ''))
            .filter(n => n.length >= 7)
    )];
    const duplicateCount = window.__globeomni_removed_duplicates__;
    const variables      = templateData.variables  || [];
    const allVarNums     = templateData.allVarNums || [];
    const bodyText       = templateData.components?.find(c => c.type === 'BODY')?.text || '';

    // Build variablesMap
    function buildVariablesMap() {
        const map = {};
        for (const number of uniqueNumbers) {
            map[number] = allVarNums.length > 0
                ? allVarNums.map(n => variables[n - 1] || '')
                : [];
        }
        return map;
    }

    // Poll queue stats every 2s after launch
    useEffect(() => {
        if (!launched) return;

        const poll = async () => {
            try {
                const res  = await fetch(`${API_URL}/auth/campaign/stats`, {
                    headers: { Authorization: `Bearer ${token || ""}` },
                });
                const data = await res.json();
                setQueueStats(data);
                const processing = (data.waiting || 0) + (data.active || 0);
                if (processing === 0) {
                    clearInterval(pollRef.current);
                    if (typeof onSuccess === 'function' && (data.failed || 0) === 0) {
                        setTimeout(onSuccess, 3000);
                    }
                }
            } catch { /* ignore poll errors */ }
        };

        pollRef.current = setInterval(poll, 2000);
        poll();
        return () => clearInterval(pollRef.current);
        // eslint-disable-next-line
    }, [launched]);

    // ── Launch (with or without schedule) ──────────────────────────
    const launchCampaign = async () => {
        if (!uniqueNumbers.length) {
            setLaunchError("No valid numbers to send to.");
            return;
        }
        if (schedule && !scheduledTime) {
            setLaunchError("Please provide a schedule date & time.");
            return;
        }
        // Validate future datetime if scheduling
        if (schedule) {
            const scheduleDate = new Date(scheduledTime);
            const now = new Date();
            if (isNaN(scheduleDate) || scheduleDate <= now) {
                setLaunchError("Scheduled time must be in the future.");
                return;
            }
        }

        setLaunching(true);
        setLaunchError(null);

        try {
            const variablesMap = buildVariablesMap();
            const  template_type = (templateData.category || templateData.templateName || templateData.templateType);

            const payload = {
                phone_number_id: campaignData.phoneNumberId,
                user_id: userId,
                waba_id: campaignData.wabaId,
                template_type: templateData?.templateType ? templateData?.templateType : "undefined",
                template_name: templateData?.templateName ? templateData?.templateName : "undefined",
                numbers: uniqueNumbers,
                variablesMap,
                campaign_name: campaignData?.campaignName,
            };

            // Add 'schedule' and 'scheduledTime' fields if scheduling is chosen
            if (schedule && scheduledTime) {
                payload.schedule = true;
                payload.scheduledTime = scheduledTime;
            }
    

            const res = await fetch(`${API_URL}/auth/campaign/create`, {
                method:  "POST",
                headers: {
                    "Content-Type":  "application/json",
                    "Authorization": `Bearer ${token || ""}`,
                },
                body: JSON.stringify(payload),
            });

            const result = await res.json();
            if (!res.ok) throw new Error(result?.error || `HTTP ${res.status}`);

            setLaunched(true);

            window.location.href = '/Report'

        } catch (err) {
            setLaunchError(err.message);
        } finally {
            setLaunching(false);
        }
    };

    // ── Summary items ──────────────────────────────────────────────
    const summaryItems = [
        { icon: <FaBullhorn />,    label: 'Campaign Name',      value: campaignData.campaignName  || '—' },
        { icon: <FaWifi />,        label: 'Channel',            value: campaignData.channelLabel  || campaignData.phoneNumberId || '—' },
        { icon: <FaScroll />,      label: 'Template',           value: templateData.templateName  || '—' },
        { icon: <FaSlidersH />,    label: 'Language',           value: templateData.language      || '—', badge: true },
        { icon: <FaAddressBook />, label: 'Total Recipients',   value: uniqueNumbers.length },
        { icon: <FaFilter />,      label: 'Duplicates Removed', value: duplicateCount },
        ...(campaignData.groupName     ? [{ icon: <FaUsers />, label: 'Group',          value: campaignData.groupName }]     : []),
        ...(campaignData.scheduledTime ? [{ icon: <FaClock />, label: 'Scheduled Time', value: campaignData.scheduledTime }] : []),
        ...(schedule && scheduledTime
            ? [{ icon: <FaClock />, label: 'Scheduled For', value: scheduledTime.replace("T", " ") }]
            : []),
    ];

    // ── Queue progress derived values ──────────────────────────────
    const totalQueued   = uniqueNumbers.length;
    const completed     = queueStats?.completed || 0;
    const failed        = queueStats?.failed    || 0;
    const active        = queueStats?.active    || 0;
    const waiting       = queueStats?.waiting   || 0;
    const processed     = completed + failed;
    const progressPct   = totalQueued > 0 ? Math.min(100, Math.round((processed / totalQueued) * 100)) : 0;
    const isDone        = launched && waiting === 0 && active === 0;

    return (
        <div className='w-[100%] shadow-[0px_5px_15px_0px_rgba(0,0,0,0.15)] p-3 md:p-4 rounded-md mt-4'>
            <div className='w-[100%] border-b-[1px] border-[#dfdfdf] pb-2 mb-4'>
                <p className='text-[#121519] font-semibold text-[16px] md:text-[18px] dark:text-[#fff]'>
                    Campaign Summary 📲
                </p>
            </div>

            {/* ── Summary grid ── */}
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4'>
                {summaryItems.map((item, i) => (
                    <div key={i} className='border rounded-lg p-3 shadow-sm bg-[#f9fafb]'>
                        <div className='flex items-center gap-2 mb-1'>
                            <span className='text-[#ed7b00]'>{item.icon}</span>
                            <strong className='text-[13px] text-[#374151]'>{item.label}</strong>
                        </div>
                        {item.badge
                            ? <span className='inline-block bg-[#d1fae5] text-[#065f46] text-[11px] px-2 py-0.5 rounded-full font-semibold'>{item.value}</span>
                            : <div className='text-[13px] text-[#6b7280] font-medium'>{item.value}</div>
                        }
                    </div>
                ))}
            </div>

            {/* ── Variable values summary ── */}
            {allVarNums.length > 0 && (
                <div className='mb-4 p-3 bg-[#fffbe6] border border-[#f59e0b] rounded-lg'>
                    <p className='text-[13px] font-bold text-[#856404] mb-2'>📝 Template Variables (sent to all recipients)</p>
                    <div className='flex flex-wrap gap-2'>
                        {allVarNums.map(n => (
                            <div key={n} className='bg-white border border-[#f59e0b] rounded-lg px-3 py-1.5'>
                                <span className='text-[10px] text-[#808080]'>{`{{${n}}}`}</span>
                                <p className='text-[12px] font-semibold text-[#374151]'>
                                    {variables[n - 1] || <span className='text-red-400 italic'>empty</span>}
                                </p>
                            </div>
                        ))}
                    </div>
                    {bodyText && (
                        <div className='mt-2 pt-2 border-t border-[#f59e0b]'>
                            <p className='text-[10px] text-[#808080] mb-1'>Message preview:</p>
                            <p className='text-[12px] text-[#374151] whitespace-pre-line'>
                                {bodyText.replace(/{{\d+}}/g, match => {
                                    const idx = parseInt(match.replace(/[{}]/g, '')) - 1;
                                    return variables[idx] ? `[${variables[idx]}]` : match;
                                })}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* ── Recipients preview ── */}
            {uniqueNumbers.length > 0 && (
                <div className='mb-4 p-3 bg-[#f0faf8] border border-[#6ee7b7] rounded-lg'>
                    <p className='text-[13px] font-bold text-[#065f46] mb-2'>
                        Recipients ({uniqueNumbers.length})
                    </p>
                    <div className='flex flex-wrap gap-2'>
                        {uniqueNumbers.slice(0, 10).map((n, i) => (
                            <span key={i} className='text-[11px] bg-white border border-[#6ee7b7] text-[#374151] px-2 py-0.5 rounded-full'>
                                {n}
                            </span>
                        ))}
                        {uniqueNumbers.length > 10 && (
                            <span className='text-[11px] text-[#808080]'>+{uniqueNumbers.length - 10} more</span>
                        )}
                    </div>
                </div>
            )}

            {/* ── Live queue progress (after launch) ── */}
            {launched && (
                <div className='mb-4 p-4 border rounded-lg bg-[#f9fafb]'>
                    <div className='flex items-center justify-between mb-3'>
                        <span className='text-[13px] font-bold text-[#374151]'>
                            Queue Progress
                            {!isDone && <span className='ml-2 animate-pulse text-[#ed7b00]'>● Processing...</span>}
                        </span>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold
                            ${isDone && failed === 0  ? 'bg-[#d1fae5] text-[#065f46]'
                            : isDone && failed > 0    ? 'bg-[#fffbe6] text-[#856404]'
                            : 'bg-[#e0f2fe] text-[#0369a1]'}`}>
                            {isDone ? (failed === 0 ? 'completed' : 'partial') : 'running'}
                        </span>
                    </div>

                    {/* Progress bar */}
                    <div className='w-full bg-[#e5e7eb] rounded-full h-2.5 mb-2'>
                        <div
                            className={`h-2.5 rounded-full transition-all duration-500 ${
                                isDone && failed > 0 ? 'bg-[#f59e0b]' :
                                isDone              ? 'bg-[#ed7b00]' : 'bg-[#ed7b00]'
                            }`}
                            style={{ width: `${progressPct}%` }}
                        />
                    </div>

                    {/* Stats row */}
                    <div className='grid grid-cols-4 gap-2 text-center hidden'>
                        {[
                            { label: 'Waiting',   value: waiting,   color: 'text-[#6b7280]' },
                            { label: 'Active',    value: active,    color: 'text-[#0369a1]' },
                            { label: 'Completed', value: completed, color: 'text-[#ed7b00]' },
                            { label: 'Failed',    value: failed,    color: 'text-[#ed5050]'  },
                        ].map((s, i) => (
                            <div key={i} className='bg-white border border-[#e5e7eb] rounded-lg p-2'>
                                <p className={`text-[16px] font-bold ${s.color}`}>{s.value}</p>
                                <p className='text-[10px] text-[#808080]'>{s.label}</p>
                            </div>
                        ))}
                    </div>

                    {isDone && failed === 0 && (
                        <p className='text-[12px] text-[#065f46] font-semibold mt-3 text-center'>
                            🎉 All {completed} messages sent successfully!
                        </p>
                    )}
                    {isDone && failed > 0 && (
                        <p className='text-[12px] text-[#856404] font-semibold mt-3 text-center'>
                            ⚠️ {completed} sent, {failed} failed. Check your queue logs.
                        </p>
                    )}
                </div>
            )}

            {/* ── Launch error ── */}
            {launchError && (
                <div className='mb-4 p-3 bg-[#fee2e2] border border-[#fca5a5] rounded-lg text-[#991b1b] text-[13px] font-semibold'>
                    ❌ {launchError}
                </div>
            )}

            {/* ── Schedule Option ── */}
            <div className='flex flex-col sm:flex-row items-end gap-3 mt-6 mb-3'>
                <label className='flex items-center gap-2 text-[13px] font-semibold text-[#374151]'>
                    <input
                        type="checkbox"
                        checked={schedule}
                        onChange={e => {
                            setSchedule(e.target.checked);
                            if (!e.target.checked) setScheduledTime("");
                        }}
                        className="accent-[#ed7b00] w-4 h-4"
                        disabled={launched}
                        style={{ minWidth: 16, minHeight: 16 }}
                    />
                    Schedule this campaign
                </label>
                {schedule && (
                    <div className='flex items-center gap-2'>
                        <label htmlFor="schedule-datetime" className='text-[12px] text-[#374151]'>
                            Send at:
                        </label>
                        <input
                            id="schedule-datetime"
                            type="datetime-local"
                            value={scheduledTime}
                            min={new Date(Date.now() + 60000).toISOString().slice(0, 16)} // at least 1 min from now
                            onChange={e => setScheduledTime(e.target.value)}
                            disabled={launched}
                            className='border border-[#d1d1d1] rounded-md px-2 py-1 text-[13px] bg-white'
                            style={{ minWidth: 210, maxWidth: 320 }}
                        />
                    </div>
                )}
            </div>

            {/* ── Actions ── */}
            <div className='flex justify-end gap-2 mt-2'>
                <button
                    onClick={onPrev}
                    disabled={launching || launched}
                    className='hover:bg-[#d3dde6] bg-[#edf1f5] text-[#434a57] p-2 rounded-md text-[13px] font-semibold flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed'
                >
                    <FaHandPointLeft /> Previous
                </button>
                <button
                    onClick={launchCampaign}
                    disabled={launching || launched || !uniqueNumbers.length || !templateData.templateName}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-bold text-white transition-colors
                        ${launching || launched || !uniqueNumbers.length || !templateData.templateName
                            ? 'bg-[#37715d]/50 cursor-not-allowed'
                            : 'bg-[#ed7b00] hover:bg-[#006857]'
                        }`}
                >
                    {launching
                        ? <><span className='animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full'></span> {schedule ? 'Scheduling...' : 'Queuing...'}</>
                        : launched
                            ? '✅ Campaign Running'
                            : (<>{schedule ? <FaClock /> : <FaPaperPlane />} {schedule ? "Schedule Campaign" :"Launch Campaign"}</>)
                    }
                </button>
            </div>
        </div>
    );
}

export default VerifySend;